import { Component, h, Prop } from '@stencil/core';

const COMPONENT_NAME = 'footer-type-5';

/**
 * @slot logo - {required}, <me-logo> - Company logo with link
 * @slot form-description - {required}, <me-text tag="div"> - Form role description
 * @slot form - {required}, <form-subscribe-type-1> - Short form
 * @slot menus - {required}, {min-items: 2}, {max-items: 5}, <footer-menu-type-1>
 * @slot socials - {required}, <me-socials> - Social media section with heading and icon links
 * @slot copyright - {required}, <me-text variant="small"> - Copyright text
 * @slot legal - {required}, {min-items: 2}, {max-items: 3}, <me-button tag="a" variant="ghost"> - Legal footer links (Privacy Policy, Terms, etc.)
 */
@Component({
  tag: 'footer-type-5',
  styleUrl: 'index.scss',
  shadow: false,
})
export class FooterType5 {
  /**
   * Background color theme. Use 'white' for light backgrounds and 'dark' for dark backgrounds.
   * @type {string}
   * @default 'white'
   * {enum}: ['white', 'dark']
   */
  @Prop() theme: 'white' | 'dark' = 'white';

  render() {
    return (
      <footer class={`${COMPONENT_NAME} ${COMPONENT_NAME}--${this.theme}`} role="contentinfo" aria-label="Site footer" itemScope itemType="https://schema.org/Organization">
        <me-container yGap="2xl" yGapLg="5xl">
          <me-row class={`${COMPONENT_NAME}__secondary`} xGap="lg" xGapLg="4xl" yGap="3xl" yGapLg="2xs" justifyLg="between" direction="column" directionLg="row">
            <me-column span="12" spanMd="2" spanLg="2" class={`${COMPONENT_NAME}__logo`}>
              <div itemProp="logo">
                <slot name="logo"></slot>
              </div>
            </me-column>
            <me-column span="12" spanMd="5" spanLg="10" class={`${COMPONENT_NAME}__menus`}>
              <slot name="menus"></slot>
            </me-column>
          </me-row>
          <me-row
            class={`${COMPONENT_NAME}__form`}
            yGap="4xl"
            yGapLg="2xs"
            direction="column"
            directionLg="row"
            align="stretch"
            alignLg="start"
            justify="start"
            justifyLg="between"
          >
            <me-column span="12" spanLg="6">
              <slot name="form-description"></slot>
            </me-column>
            <me-column span="12" spanLg="6">
              <slot name="form"></slot>
            </me-column>
          </me-row>
          <me-row
            class={`${COMPONENT_NAME}__row-third`}
            yGap="xl"
            yGapLg="2xl"
            direction="column"
            directionLg="column"
            align="stretch"
            alignLg="stretch"
            justify="start"
            justifyLg="between"
          >
            <me-column>
              <div class={`${COMPONENT_NAME}__divider`} role="separator" aria-hidden="true"></div>
            </me-column>
            <me-column>
              <me-row xGap="lg" xGapLg="xl" yGap="xl" yGapLg="xl" align="start" alignLg="center" direction="column" directionLg="row" justify="start" justifyLg="between">
                <me-column span="12" spanLg="auto" class={`${COMPONENT_NAME}__legal-copyright`}>
                  <me-row xGap="lg" xGapLg="xl" yGap="xl" yGapMd="2xs" yGapLg="2xs" direction="column" directionLg="row" align="start" alignLg="center">
                    <me-column span="12">
                      <slot name="copyright"></slot>
                    </me-column>
                    <me-column span="12" class={`${COMPONENT_NAME}__legal-links`}>
                      <slot name="legal"></slot>
                    </me-column>
                  </me-row>
                </me-column>
                <me-column span="12" spanLg="auto" class={`${COMPONENT_NAME}__socials-language-switcher`}>
                  <div class={`${COMPONENT_NAME}__social-links`} itemProp="sameAs">
                    <slot name="socials"></slot>
                  </div>
                  <div>
                    <slot name="language-switcher"></slot>
                  </div>
                </me-column>
              </me-row>
            </me-column>
          </me-row>
        </me-container>
      </footer>
    );
  }
}
